import { createApi } from "@reduxjs/toolkit/query/react";
import {
  ApiResponse,
  AuditLogsResponse,
  BusinessLinesData,
  SupportedCurrenciesData,
} from "model/response-model";
import { baseQueryWithReauth } from "./config/interceptor";
import {
  AddBulkImportModel,
  BulkImportApiResponse,
  CreateConversionRatesModel,
  CreateCurrencyRuleModel,
  EditConversionRatesModel,
  EditCurrencyRuleModel,
} from "model/rule-engine-model";

// Define a service using a base URL and expected endpoints
export const ruleEngineApi = createApi({
  reducerPath: "ruleEngineApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["CurrencyRules"],
  endpoints: (builder) => ({
    getCurrencyRule: builder.query<ApiResponse<null>, void>({
      query: () => ({
        url: "CurrencyRule",
        method: "GET",
      }),
    }),

    getSupportedCurrencies: builder.query<
      ApiResponse<SupportedCurrenciesData>,
      void
    >({
      query: () => ({
        url: "CurrencyRule/supported-currencies",
        method: "GET",
      }),
    }),

    deleteCurrencyRule: builder.mutation<ApiResponse<string>, string>({
      query: (userId) => ({
        url: `CurrencyRule/delete/${userId}`,
        method: "DELETE",
      }),
    }),

    deleteConversionRate: builder.mutation<ApiResponse<string>, string>({
      query: (userId) => ({
        url: `CurrencyRule/delete-conversion-rates/${userId}`,
        method: "DELETE",
      }),
    }),

    getBusinessLines: builder.query<ApiResponse<BusinessLinesData>, void>({
      query: () => ({
        url: "CurrencyRule/business-lines",
        method: "GET",
      }),
    }),

    getAuditLogs: builder.query<
      ApiResponse<AuditLogsResponse>,
      { page: number }
    >({
      query: ({ page }) => ({
        url: `CurrencyRule/audit-logs?page=${page}`,
        method: "GET",
      }),
    }),

    getConversionRates: builder.query<ApiResponse<null>, void>({
      query: () => ({
        url: "CurrencyRule/get-conversion-rates",
        method: "GET",
      }),
    }),

    createCurrencyRule: builder.mutation<
      ApiResponse<null>,
      CreateCurrencyRuleModel
    >({
      query: (currencyRuleData) => ({
        url: "CurrencyRule/create",
        method: "POST",
        body: currencyRuleData,
      }),
    }),

    createConversionRate: builder.mutation<
      ApiResponse<null>,
      CreateConversionRatesModel
    >({
      query: (conversionRateDate) => ({
        url: "CurrencyRule/create-conversion-rates",
        method: "POST",
        body: conversionRateDate,
      }),
    }),

    addBulkImport: builder.mutation<BulkImportApiResponse, AddBulkImportModel>({
      query: (extractedBulkImport) => ({
        url: "CurrencyRule/bulk-import",
        method: "POST",
        body: extractedBulkImport,
      }),
    }),

    editCurrencyRule: builder.mutation<
      ApiResponse<null>,
      EditCurrencyRuleModel
    >({
      query: (editData) => ({
        url: `CurrencyRule/update/${editData.ruleId}`,
        method: "PUT",
        body: editData,
      }),
    }),

    editConversionRate: builder.mutation<
      ApiResponse<null>,
      EditConversionRatesModel
    >({
      query: (editData) => ({
        url: `CurrencyRule/update-conversion-rates/${editData.rateId}`,
        method: "PUT",
        body: editData,
      }),
    }),

    getCurrentCurrencyRuleHistory: builder.query<
      ApiResponse<any>,
      string | undefined
    >({
      query: (ruleId) => ({
        url: `CurrencyRule/get/${ruleId}`,
        method: "GET",
      }),
    }),

    getCurrentCurrencyRuleVersions: builder.query<
      ApiResponse<any>,
      string | undefined
    >({
      query: (ruleId) => ({
        url: `CurrencyRule/get-versions${ruleId}`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useCreateCurrencyRuleMutation,
  useEditCurrencyRuleMutation,
  useGetCurrencyRuleQuery,
  useGetCurrentCurrencyRuleHistoryQuery,
  useGetAuditLogsQuery,
  useGetConversionRatesQuery,
  useCreateConversionRateMutation,
  useEditConversionRateMutation,
  useGetBusinessLinesQuery,
  useGetSupportedCurrenciesQuery,
  useDeleteConversionRateMutation,
  useDeleteCurrencyRuleMutation,
  useAddBulkImportMutation,
  useGetCurrentCurrencyRuleVersionsQuery,
} = ruleEngineApi;
